import { productPricingService } from "../../services/productPricingService";
import { DisplayVariant, PriceDisplayType, ProductPricingInput, PRICE_UPDATE_EVENT, SelectionsWithQuantity } from "../../types/pricing/price-display.types";
import { EstimatedPrice, ProductPriceDetails, QuantityMetaData } from "../../types/pricing/product-price-details.types";
import { calculateEvaluatedQuantity } from "../../util/price-extraction-helper";
import { getPriceQueryId } from "../../util/price-queryId";
import { sanitizeSelections } from "../../util/sanitizeSelections";
import { priceEventTarget } from "./getProductPriceData";

export type FullProductPriceDetailsInput = {
  mpvId?: string;
  productKey?: string;
  productId?: string;
  version?: number;
  selections?: SelectionsWithQuantity;
  quantity?: number;
  alternateSelections?: SelectionsWithQuantity;
  displayVariant?: DisplayVariant;
  requestedPriceDisplayStrategy?: PriceDisplayType;
  docRefUrl?: string;
  assumeDocRequiresCertification?: boolean;
};

export type FullProductPriceDetailsResult = {
  estimatedPrice: EstimatedPrice;
  quantity: number;
  currency: string;
  fractionDigits: number;
  quantityMetaData?: QuantityMetaData;
  alternateQuantityMetaData?: QuantityMetaData;
  displayStrategy: PriceDisplayType;
  priceDisplayGroup?: string;
  productId?: string;
  mpvId?: string;
  url: string;
};

const buildQueryInput = (input: FullProductPriceDetailsInput): Pick<
  ProductPricingInput,
  "mpvId" | "productId" | "version" | "selections" | "alternateSelections" | "displayVariant" | "requestedPriceDisplayStrategy" | "docRefUrl" | "assumeDocRequiresCertification"
> => {
  const { mpvId, productId, productKey, version, selections, quantity, alternateSelections, displayVariant, requestedPriceDisplayStrategy, docRefUrl, assumeDocRequiresCertification } = input;

  const mergedSelections = sanitizeSelections(selections, quantity);

  return {
    mpvId,
    productId: productId ?? productKey,
    version,
    selections: mergedSelections,
    alternateSelections,
    displayVariant,
    requestedPriceDisplayStrategy,
    docRefUrl,
    assumeDocRequiresCertification,
  };
};

/**
 * Returns pricing details for a given set of query props (the same props used
 * by PriceDisplay). Returns the EstimatedPrice for the requested quantity, or
 * the first available entry if the requested quantity is not found.
 *
 * Does NOT trigger a new API call — it expects the data to be fetched by a
 * PriceDisplay (or equivalent) on the same page.
 */
export const getFullProductPriceDetails = (
  input: FullProductPriceDetailsInput,
  timeoutMs = 5000,
): Promise<FullProductPriceDetailsResult> => {
  const queryInput = buildQueryInput(input);
  const queryId = getPriceQueryId(queryInput);
  const requestedQuantity = input.quantity ?? (input.selections?.quantity ? Number(input.selections.quantity) : undefined);

  if (!queryId) {
    return Promise.reject(new Error("Unable to generate queryId from provided input"));
  }

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error("Price details unavailable after timeout"));
    }, timeoutMs);

    const cleanup = () => {
      clearTimeout(timeoutId);
      priceEventTarget.removeEventListener(PRICE_UPDATE_EVENT, listener);
    };

    const tryResolve = (details: Record<string, ProductPriceDetails>): boolean => {
      const match = details[queryId];
      if (!match || match.isError) return false;

      const evaluatedQuantity = calculateEvaluatedQuantity(match, requestedQuantity?.toString(), undefined);
      const estimatedPrice = match.prices.estimatedPrices[Number(evaluatedQuantity)];
      if (!estimatedPrice) return false;

      cleanup();
      resolve({
        estimatedPrice,
        quantity: Number(evaluatedQuantity),
        currency: match.prices.currency,
        fractionDigits: match.prices.fractionDigits,
        quantityMetaData: match.quantityMetaData,
        alternateQuantityMetaData: match.alternateQuantityMetaData,
        displayStrategy: match.displayStrategy,
        priceDisplayGroup: match.priceDisplayGroup,
        productId: match.productId,
        mpvId: match.mpvId,
        url: match.prices.url,
      });
      return true;
    };

    const listener = (event: Event) => {
      const { detail } = event as CustomEvent<Record<string, ProductPriceDetails>>;
      tryResolve(detail);
    };

    // Check current cache first
    if (tryResolve(productPricingService.pricingDetails)) {
      return;
    }

    // Listen for future updates
    priceEventTarget.addEventListener(PRICE_UPDATE_EVENT, listener);
  });
};
